import {Input, Form, Modal, Col, Row, InputNumber} from "antd";
import {useFormik} from "formik";

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {useStore} from "../../../../hooks/useStore";

type ObjectionRevisionModalProps = {
    shiftScore: {},
    visibility: Boolean,
    onCancel: () => void,
    closeModal: () => void
};

const ObjectionRevisionModal = ({
    objection,
    shiftScore,
    visibility,
    onCancel,
    closeModal
}): ObjectionRevisionModalProps => {
    const {t} = useTranslation([
        "shiftScore",
        "objectionDetail",
        "courierStatuses",
        "courierStatusUpdateModal",
        "changeStatusList"
    ]);
    const stores = useStore();
    const [loading, setLoading] = useState();
    const [hasDiffObjectionValues, setHasDiffObjectionValues] = useState(true);
    const {
        _id = "",
        reportDate = "",
        fullname = "",
        tckn = "",
        cellPhone = "",
        companyName = "",
        companyCode = "",
        subCompanyName = "",
        subCompanyCode = "",
        shiftStartTime = "",
        shiftEndTime = "",
        startTime = "",
        endTime = "",
        workTimeMinute = "",
        busyTimeMinute = "",
        packageCount = ""
    } = shiftScore || {};

    const formik = useFormik({
        initialValues: {
            objectionId: objection?._id,
            workTimeMinute: objection?.workTimeMinute || 0,
            packageCount: objection?.packageCount || 0
        },
        validationSchema: yup.object().shape({
            workTimeMinute: yup.string().required(t("validation.required", {ns: "common"})),
            packageCount: yup.string().required(t("validation.required", {ns: "common"}))
        }),
        onSubmit: async values => {
            if (!checkDiffWithObjectionValues({})) {
                return;
            }
            setLoading(true);
            await stores.shiftStore.updateShiftScoreObjectionRevision(values);
            await resetForm();

            setLoading(false);
            closeModal();
        },
        enableReinitialize: true
    });

    const {values, errors, touched, handleChange, handleSubmit, resetForm, setFieldValue} = formik;

    const checkDiffWithObjectionValues = ({
        packageCount = values.packageCount,
        workTimeMinute = values.workTimeMinute
    }) => {
        if (
            packageCount === objection?.packageCount &&
            workTimeMinute === objection?.workTimeMinute
        ) {
            setHasDiffObjectionValues(false);
            return false;
        } else {
            setHasDiffObjectionValues(true);
            return true;
        }
    };

    return (
        <Modal
            title={t("objectionDetail.revisionModal.title", {ns: "objectionDetail"})}
            visible={visibility}
            onCancel={onCancel}
            confirmLoading={loading}
            width={630}
            okText={t("actions.update", {ns: "common"})}
            onOk={handleSubmit}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
            data-testid="createShiftReportObjection"
        >
            <Row justify="space-between">
                <Col>
                    <p>
                        <b>{t("fields.date", {ns: "common"})}</b> : {reportDate}
                    </p>
                    <p>
                        <b>{t("fields.courier", {ns: "common"})}</b> : {fullname}
                    </p>
                    <p>
                        <b>{t("fields.courierIdNo", {ns: "common"})}</b> : {tckn}
                    </p>
                    <p>
                        <b>{t("fields.courierPhone", {ns: "common"})}</b> : {cellPhone}
                    </p>
                    <p>
                        <b>{t("fields.company", {ns: "common"})}</b> : {companyName}
                    </p>
                    <p>
                        <b>{t("fields.companyCode", {ns: "common"})}</b> : {companyCode}
                    </p>
                    <p>
                        <b>{t("fields.subcompany", {ns: "common"})}</b> : {subCompanyName}
                    </p>
                    <p>
                        <b>{t("fields.subCompanyCode", {ns: "common"})}</b> : {subCompanyCode}
                    </p>
                </Col>
                <Col>
                    <p>
                        <b>{t("shiftScore.shiftStartTime", {ns: "shiftScore"})}</b> :{" "}
                        {shiftStartTime}
                    </p>
                    <p>
                        <b>{t("shiftScore.shiftEndTime", {ns: "shiftScore"})}</b> : {shiftEndTime}
                    </p>
                    <p>
                        <b>{t("shiftScore.startTime", {ns: "shiftScore"})}</b> : {startTime}
                    </p>

                    <p>
                        <b>{t("shiftScore.endTime", {ns: "shiftScore"})}</b> : {endTime}
                    </p>
                    <p>
                        <b>
                            {t("shiftScore.workTime", {ns: "shiftScore"})} - (
                            {t("units.minute.short", {ns: "common"})})
                        </b>{" "}
                        : {workTimeMinute}
                    </p>
                    {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                        <>
                            <p>
                                <b>
                                    {t("shiftScore.busyTimeMinute", {ns: "shiftScore"})} - (
                                    {t("units.minute.short", {ns: "common"})})
                                </b>{" "}
                                : {busyTimeMinute}
                            </p>
                        </>
                    )}
                    <p>
                        <b>{t("shiftScore.packageCount", {ns: "shiftScore"})}</b> : {packageCount}
                    </p>
                </Col>

                <Col>
                    <Form autoComplete="off" labelCol={{span: 24}} wrapperCol={{span: 24}}>
                        <Form.Item
                            colon={false}
                            label={`${t("objectionDetail.revisionModal.fields.revisionWorkTime", {
                                ns: "objectionDetail"
                            })} :`}
                            labelAlign="left"
                            validateStatus={
                                (errors.workTimeMinute && touched.workTimeMinute) ||
                                !hasDiffObjectionValues
                                    ? "error"
                                    : "success"
                            }
                            help={`${
                                errors.workTimeMinute && touched.workTimeMinute
                                    ? errors.workTimeMinute
                                    : ""
                            } ${
                                !hasDiffObjectionValues
                                    ? t(
                                          "objectionDetail.revisionModal.errors.atLeastOneDifferent",
                                          {ns: "objectionDetail"}
                                      )
                                    : ""
                            }`}
                        >
                            <InputNumber
                                cols={120}
                                rows={4}
                                name="workTimeMinute"
                                value={values.workTimeMinute}
                                onChange={value => {
                                    setFieldValue(`workTimeMinute`, value || 0);
                                    checkDiffWithObjectionValues({workTimeMinute: value});
                                }}
                                data-testid="workTimeMinute"
                            />
                        </Form.Item>
                        <Form.Item
                            colon={false}
                            label={`${t(
                                "objectionDetail.revisionModal.fields.revisionPackageCount",
                                {
                                    ns: "objectionDetail"
                                }
                            )} :`}
                            labelAlign="left"
                            validateStatus={
                                (errors.packageCount && touched.packageCount) ||
                                !hasDiffObjectionValues
                                    ? "error"
                                    : "success"
                            }
                            help={`${
                                errors.packageCount && touched.packageCount
                                    ? errors.packageCount
                                    : ""
                            } ${
                                !hasDiffObjectionValues
                                    ? t(
                                          "objectionDetail.revisionModal.errors.atLeastOneDifferent",
                                          {ns: "objectionDetail"}
                                      )
                                    : ""
                            }`}
                        >
                            <InputNumber
                                name="packageCount"
                                value={values.packageCount}
                                onChange={value => {
                                    setFieldValue(`packageCount`, value || 0);
                                    checkDiffWithObjectionValues({packageCount: value});
                                }}
                                data-testid="packageCount"
                            />
                        </Form.Item>
                    </Form>
                </Col>
            </Row>
        </Modal>
    );
};

export default ObjectionRevisionModal;
